import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldType } from 'types/FieldTypes';
import FormModal from 'components/modals/FormModal';
import { useModal } from 'components/modals/ModalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { fetchData } from 'services/api/fetchData';
import { useTimer } from 'services/timer/TimerContext';

interface TimeRowFormProps {
    viewKey?: string;
    stopTimer?: boolean; // Opens the form as the stop form of a running timer
}

// The edit form of a time row, which turns into the stop form for a running timer
const TimeRowForm: React.FC<TimeRowFormProps> = ({ viewKey, stopTimer }) => {
    const { t } = useTranslation();
    const { handleLogout } = useAuthContext();
    const { modalStack, updateModalProps } = useModal();
    const { runningTimer, refreshTimer } = useTimer();
    const [isRunning, setIsRunning] = useState<boolean | null>(stopTimer ? true : null);
    const topModal = modalStack[modalStack.length - 1];
    const itemId = topModal?.props?.itemId;

    // Keep the logout handler in a ref, its identity changes on every render
    const handleLogoutRef = useRef(handleLogout);
    handleLogoutRef.current = handleLogout;

    // Determine wheter the opened time row is a running timer when it is not known yet
    useEffect(() => {
        if (isRunning !== null) return;
        if (!itemId) {
            setIsRunning(false);
            return;
        }
        const checkRunning = async () => {
            try {
                const data = await fetchData({ apiUrl: `get_timerow/${itemId}`, handleLogout: handleLogoutRef.current });
                setIsRunning(data?.status === 'running');
            } catch (error) {
                setIsRunning(false);
            }
        };
        checkRunning();
        // eslint-disable-next-line
    }, [itemId]);

    // Give the stop form its own title
    useEffect(() => {
        if (isRunning && topModal) {
            updateModalProps(topModal.key, { title: t('timerow.timer.stop_title') });
        }
        // eslint-disable-next-line
    }, [isRunning]);

    // Wait until it is known wheter the time row is running
    if (isRunning === null) return null;

    // The current local time as default end time of a stopped timer
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

    // The fields of the form, the stop form locks the date and hides the approval
    const fields: FieldType[] = [
        {
            type: 'dropdown',
            name: 'time_type',
            label: 'timerow.general.type_label',
            apiRequest: [{
                endpoint: 'get_timesheet_timetype_list',
                object: 'timesheet_timetype',
                params: { is_active: true },
                useCache: true
            }],
            disabled_selected: 'timerow.general.type_disabled_selected',
            allowNoneOption: false,
            showSearch: false,
        },
        {
            type: 'searchselect',
            name: 'project',
            label: 'timerow.general.project_label',
            placeholder: 'job.general.project_placeholder',
            objectName: 'timerow.general.object_name.singular',
            query: { endpoint: 'get_project_search' },
            activeFeature: 'projects',
            disableAddNew: true,
            selectionFormat: 'name',
            optionFormat: {
                title: { field: 'name', format: (value) => value },
                subtitle: { field: 'number', format: (value) => value, optional: true},
            },
        },
        {
            type: 'searchselect',
            name: 'job',
            label: 'timerow.general.job_label',
            placeholder: 'timerow.general.job_placeholder',
            objectName: 'job.general.object_name.singular',
            query: { endpoint: 'get_job_search' },
            activeFeature: 'jobs',
            disableAddNew: true,
            selectionFormat: 'number',
            optionFormat: { title: { field: 'number', format: (value) => value } },
        },
        {
            type: 'searchselect',
            name: 'ticket',
            label: 'timerow.general.ticket_label',
            placeholder: 'timerow.general.ticket_placeholder',
            objectName: 'ticket.general.object_name.singular',
            query: { endpoint: 'get_ticket_list', params: { deleted: false } },
            activeFeature: 'tickets',
            disableAddNew: true,
            showIfFilled: true,
            selectionFormat: 'number',
            optionFormat: {
                title: { field: 'number', format: (value) => t('ticket.general.ticket_title', { number: value }) },
                subtitle: { field: 'subject', format: (value) => value, optional: true },
            },
        },
        {
            type: 'date',
            name: 'start_date',
            label: 'timerow.general.date_label',
            convertTimezone: true,
            utcDatetime: 'start_datetime',
            disabled: !!isRunning,
        },
        {
            type: 'time',
            name: 'start_time',
            label: 'timerow.general.start_time_label',
            width: '34%',
            group: 'field-row',
            convertTimezone: true,
            utcDatetime: 'start_datetime'
        },
        {
            type: 'time',
            name: 'end_time',
            label: 'timerow.general.end_time_label',
            width: '34%',
            group: 'field-row',
            convertTimezone: true,
            utcDatetime: 'end_datetime',
            ...(isRunning ? { defaultValue: currentTime } : {}),
        },
        {
            type: 'time',
            name: 'break_duration_time',
            label: 'timerow.general.break_label',
            width: '32%',
            group: 'field-row',
        },
        {
            type: 'textarea',
            name: 'note',
            label: 'timerow.general.note_label',
            rows: 3,
            isNote: true,
            placeholder: 'timerow.general.note_placeholder',
        },
        {
            type: 'dropdown',
            name: 'assignee',
            label: 'timerow.general.assignee_label',
            apiRequest: [{
                endpoint: 'get_user_list',
                object: 'user',
                params: { is_active: true },
                useCache: true
            }],
            disabled_selected: 'timerow.general.type_disabled_selected',
            disabled: true,
            allowNoneOption: false,
            showSearch: false,
            optionFormat: 'full_name',
            selectionFormat: 'full_name'
        },
        ...(!isRunning ? [{
            name: 'approved',
            label: 'timerow.general.approved_label',
            type: 'switch',
        } as FieldType] : []),
    ];

    // Refresh the timer of the menu bar when the own running timer is stopped
    const handleSuccess = () => {
        if (isRunning && runningTimer?.id === itemId) refreshTimer();
    };

    return (
        <FormModal
            viewKey={viewKey}
            apiObject='timerow'
            fields={fields}
            linkedItem={isRunning ? { stop_timer: 'true' } : undefined}
            primaryButtonLabel={isRunning ? 'timerow.timer.stop_button_label' : undefined}
            showDeleteButton={true}
            onSuccess={handleSuccess}
        />
    );
};

export default TimeRowForm;
